import { ReactNode, ComponentProps, CSSProperties } from "react";

// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Button as AntdButton } from "antd";
import { clsx } from "clsx";

import { RVIcon, RVIconSizeOptions, type RVIconSizes } from "../../atoms/RVIcon/RVIcon";

import styles from "./RVButton.module.scss";

export const LINK_VARIANTS = {
    TEXT_LINK: "text-link",
    TEXT_LINK_LIGHT: "text-link-light",
} as const;

/**
 * For antd Tooltips to work well on disabled buttons, we need this workaround from https://github.com/react-component/tooltip/issues/18#issuecomment-411476678
 * */
export function getTooltipButtonStyles(disabled?: boolean): CSSProperties {
    return { pointerEvents: disabled ? "none" : "auto" };
}

/**
 * For antd Tooltips to work well on disabled buttons, we need this workaround from https://github.com/react-component/tooltip/issues/18#issuecomment-411476678
 * */
export function getTooltipButtonContainerStyles(disabled?: boolean): CSSProperties {
    return { cursor: disabled ? "not-allowed" : "pointer" };
}

export type RVButtonProps = ComponentProps<typeof AntdButton> & {
    /** Optional link style variant (text-link, text-link-light) */
    linkVariant?: (typeof LINK_VARIANTS)[keyof typeof LINK_VARIANTS];
    /** Optional text variant (e.g., "icon") */
    textVariant?: "icon";
    /** Button content (text or elements) */
    children?: ReactNode | ReactNode[];
    /** Icon name (string) or JSX element to display */
    icon?: string | JSX.Element;
    /** Size of the icon (from RVIconSizes) */
    iconSize?: RVIconSizes;
    /** When true, uses the small button's corner radius (4px) instead of the pill radius, while keeping the button size */
    squaredCorners?: boolean;
};

export const clsRaisedButton = styles.raised;

/**
 * RVButton is a styled button component based on Ant Design's Button, with support for icons, link variants, and custom styling.
 *
 * - Supports all Antd Button props
 * - Can display an icon (by name or JSX)
 * - Supports link and text variants for flexible UI
 * - Integrates with RVIcon for consistent iconography
 *
 * @example
 * // Basic usage
 * <RVButton>Click me</RVButton>
 *
 * // With icon
 * <RVButton icon="chat">Chat</RVButton>
 *
 * // As a text link
 * <RVButton linkVariant="text-link">Link</RVButton>
 *
 * // Icon only
 * <RVButton icon="settings" textVariant="icon" />
 *
 * // Large button with the small button's (4px) corners
 * <RVButton size="large" squaredCorners>Save</RVButton>
 */
export function RVButton({
    children,
    type = "default",
    linkVariant,
    textVariant,
    icon,
    iconSize,
    squaredCorners,
    className = "",
    ...props
}: RVButtonProps): JSX.Element {
    const usedIcon =
        typeof icon === "string" ? <RVIcon icon={icon} size={iconSize ?? RVIconSizeOptions.BUTTON} /> : icon;

    return (
        <AntdButton
            type={type}
            icon={usedIcon}
            className={clsx(
                styles.button,
                {
                    [styles[type]]: !!type,
                    [styles[linkVariant as string]]: !!linkVariant,
                    [styles[textVariant as string]]: !!textVariant,
                    [styles.squaredCorners]: !!squaredCorners,
                },
                className
            )}
            {...props}
        >
            {children && <div className={styles.innerText}>{children}</div>}
        </AntdButton>
    );
}

RVButton.Group = AntdButton.Group;
